import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle2,
  Clock3,
  ExternalLink,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  MessageSquare,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  TriangleAlert,
  UserRoundCheck,
  Wifi,
} from 'lucide-react';
import { fetchAdminSettings, updateSenderIdentity, type AdminSettingsApiError, type AdminSettingsSnapshot } from '@/api/adminSettings';
import { IntegrationStatusCard } from '@/components/admin/settings/IntegrationStatusCard';
import { PageHeader } from '@/components/layouts/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';

const dateTimeFormatter = new Intl.DateTimeFormat('el-GR', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Europe/Athens',
});

function formatDateTime(value: string | null): string {
  if (!value) return 'Δεν υπάρχει ακόμη';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
}

function SettingsLoadingState() {
  return (
    <div className="space-y-6" aria-label="Φόρτωση ρυθμίσεων">
      <Skeleton className="h-24 w-full rounded-2xl" />
      <Skeleton className="h-44 w-full rounded-2xl" />
      <div className="grid gap-4 xl:grid-cols-3">
        <Skeleton className="h-72 rounded-2xl xl:col-span-2" />
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    </div>
  );
}

export default function SiteSettings() {
  const { toast } = useToast();
  const [snapshot, setSnapshot] = useState<AdminSettingsSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const applySnapshot = useCallback((next: AdminSettingsSnapshot) => {
    setSnapshot(next);
    setSenderName(next.email.senderName);
    setSenderEmail(next.email.senderEmail);
    setFieldErrors({});
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setLoadError(null);

    try {
      applySnapshot(await fetchAdminSettings());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Δεν φορτώθηκαν οι ρυθμίσεις.';
      setLoadError(message);
      if (silent) toast({ title: 'Ο έλεγχος δεν ολοκληρώθηκε', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [applySnapshot, toast]);

  useEffect(() => { void load(); }, [load]);

  const isDirty = useMemo(() => Boolean(snapshot) && (
    senderName.trim() !== snapshot?.email.senderName ||
    senderEmail.trim().toLowerCase() !== snapshot?.email.senderEmail
  ), [senderEmail, senderName, snapshot]);

  const saveSender = async (event: FormEvent) => {
    event.preventDefault();
    setFieldErrors({});

    const nextErrors: Record<string, string> = {};
    if (senderName.trim().length < 2) nextErrors.sender_name = 'Συμπληρώστε το όνομα αποστολέα.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail.trim())) nextErrors.sender_email = 'Συμπληρώστε έγκυρο email.';
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      const updated = await updateSenderIdentity({ senderName: senderName.trim(), senderEmail: senderEmail.trim() });
      applySnapshot(updated);
      toast({ title: 'Η ταυτότητα αποστολέα ενημερώθηκε', description: 'Τα credentials και οι υπόλοιπες integrations δεν μεταβλήθηκαν.' });
    } catch (error) {
      const apiError = error as AdminSettingsApiError;
      if (apiError.fields) setFieldErrors(apiError.fields);
      toast({
        title: 'Δεν αποθηκεύτηκε η αλλαγή',
        description: error instanceof Error ? error.message : 'Δοκιμάστε ξανά.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !snapshot) return <SettingsLoadingState />;

  if (!snapshot) {
    return (
      <div className="space-y-6">
        <PageHeader title="Ρυθμίσεις" description="Integrations, ειδοποιήσεις και ασφαλής κατάσταση παρόχων" icon={<Settings />} iconGradient="blue" />
        <Alert variant="destructive">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Δεν φορτώθηκαν οι ρυθμίσεις</AlertTitle>
          <AlertDescription className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{loadError || 'Η υπηρεσία δεν είναι προσωρινά διαθέσιμη.'}</span>
            <Button type="button" variant="outline" onClick={() => void load()} className="min-h-11">Δοκιμή ξανά</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const readinessPercent = snapshot.readiness.total > 0
    ? Math.round((snapshot.readiness.ready / snapshot.readiness.total) * 100)
    : 0;
  const mapsCoverage = snapshot.maps.activePartners > 0
    ? Math.round((snapshot.maps.geocodedPartners / snapshot.maps.activePartners) * 100)
    : 0;
  const accessPercent = snapshot.partnerAccess.activePartners > 0
    ? Math.round((snapshot.partnerAccess.credentialedPartners / snapshot.partnerAccess.activePartners) * 100)
    : 0;

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Integrations & ρυθμίσεις"
        description="Κατάσταση παραγωγής, operational evidence και ασφαλείς αλλαγές χωρίς έκθεση credentials"
        icon={<Settings />}
        iconGradient="blue"
        actions={(
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button type="button" variant="outline" onClick={() => void load(true)} disabled={refreshing} className="min-h-11 flex-1 sm:flex-none">
              {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Νέος έλεγχος
            </Button>
            <Button asChild className="min-h-11 flex-1 sm:flex-none">
              <Link to="/admin/settings/email-test"><Mail className="mr-2 h-4 w-4" />Δοκιμή email</Link>
            </Button>
          </div>
        )}
      />

      {loadError && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-950">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Τα δεδομένα δεν ανανεώθηκαν</AlertTitle>
          <AlertDescription>Εμφανίζεται το τελευταίο επιτυχημένο snapshot. {loadError}</AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden border-0 bg-slate-950 text-white shadow-none">
        <CardContent className="grid gap-7 p-5 sm:p-7 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:items-end">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="gap-1.5 border border-emerald-400/20 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/10">
                <ShieldCheck className="h-3.5 w-3.5" /> Secrets protected
              </Badge>
              <Badge className="border border-white/10 bg-white/5 text-slate-300 hover:bg-white/5">Production</Badge>
            </div>
            <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Κρίσιμες integrations</p>
            <div className="mt-2 flex items-end gap-3">
              <strong className="text-4xl font-bold tracking-tight sm:text-5xl">{snapshot.readiness.ready}/{snapshot.readiness.total}</strong>
              <span className="pb-1 text-sm text-slate-300">ρυθμισμένες</span>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
              Η σελίδα επιστρέφει μόνο readiness, counters και την εγκεκριμένη ταυτότητα αποστολέα. Κανένα SMTP, Twilio, Maps ή Meta secret δεν φτάνει στον browser.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Συνολική ετοιμότητα</span>
              <span className="font-bold tabular-nums text-white">{readinessPercent}%</span>
            </div>
            <Progress value={readinessPercent} className="mt-3 h-2 bg-white/10" />
            <div className="mt-4 flex items-start gap-2 text-xs leading-5 text-slate-400">
              <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Snapshot {formatDateTime(snapshot.generatedAt)} ώρα Ελλάδας</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert className="border-sky-200 bg-sky-50 text-sky-950">
        <LockKeyhole className="h-4 w-4" />
        <AlertTitle>Οι provider αλλαγές παραμένουν συντονισμένες</AlertTitle>
        <AlertDescription>
          Rotation για Twilio, Google, SMTP ή Meta γίνεται provider-side και server-side με άμεσο verification και rollback. Δεν υπάρχει κουμπί που μπορεί να αντικαταστήσει κατά λάθος ενεργά credentials.
        </AlertDescription>
      </Alert>

      <section aria-labelledby="integration-status-title">
        <div className="mb-4">
          <h2 id="integration-status-title" className="text-lg font-bold text-slate-950 sm:text-xl">Κατάσταση integrations</h2>
          <p className="mt-1 text-sm text-slate-500">Το «Ρυθμισμένο» σημαίνει ότι υπάρχει πλήρης server configuration. Τα counters δίνουν ξεχωριστό operational evidence.</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <IntegrationStatusCard
            className="xl:col-span-2"
            title="Email ειδοποιήσεις"
            description={`${snapshot.email.senderName} <${snapshot.email.senderEmail || 'χωρίς sender'}>`}
            icon={<Mail className="h-5 w-5" />}
            configured={snapshot.email.configured}
            evidence={snapshot.email.lastSentAt ? `Τελευταία επιτυχής αποστολή: ${formatDateTime(snapshot.email.lastSentAt)}.` : 'Δεν υπάρχει ακόμη καταγεγραμμένη επιτυχής αποστολή στην durable queue.'}
            metrics={[
              { label: 'Στάλθηκαν', value: snapshot.email.sent },
              { label: 'Σε ουρά', value: snapshot.email.queued },
              { label: 'Απέτυχαν', value: snapshot.email.failed, tone: snapshot.email.failed > 0 ? 'danger' : 'default' },
            ]}
            href="/admin/email/statistics"
            linkLabel="Στατιστικά email"
          />

          <IntegrationStatusCard
            title="SMS συνεργατών"
            description={`Quiet hours ${snapshot.sms.quietHours || '20:00–08:00'}`}
            icon={<MessageSquare className="h-5 w-5" />}
            configured={snapshot.sms.configured}
            evidence={snapshot.sms.lastSentAt ? `Τελευταία επιτυχής αποστολή: ${formatDateTime(snapshot.sms.lastSentAt)}.` : 'Δεν υπάρχει ακόμη καταγεγραμμένη επιτυχής αποστολή στην durable queue.'}
            metrics={[
              { label: 'Στάλθηκαν', value: snapshot.sms.sent },
              { label: 'Σε ουρά', value: snapshot.sms.queued },
              { label: 'Απέτυχαν', value: snapshot.sms.failed, tone: snapshot.sms.failed > 0 ? 'danger' : 'default' },
            ]}
            href="/admin/messages/statistics"
            linkLabel="Στατιστικά SMS"
          />

          <IntegrationStatusCard
            title="Google Maps"
            description="Geocoding για radius-based ανάθεση"
            icon={<MapPin className="h-5 w-5" />}
            configured={snapshot.maps.configured}
            evidence={`${snapshot.maps.geocodedPartners} από ${snapshot.maps.activePartners} ενεργούς συνεργάτες έχουν επαληθευμένες συντεταγμένες.`}
            metrics={[
              { label: 'Κάλυψη', value: `${mapsCoverage}%` },
              { label: 'Geocoded', value: snapshot.maps.geocodedPartners },
            ]}
            href="/admin/partners/map"
            linkLabel="Χάρτης δικτύου"
          />

          <IntegrationStatusCard
            title="Meta tracking"
            description="Pixel, CAPI και CRM outcome feedback"
            icon={<Wifi className="h-5 w-5" />}
            configured={snapshot.meta.pixelConfigured && snapshot.meta.capiConfigured}
            statusLabel={snapshot.meta.testMode ? 'Test mode ενεργό' : undefined}
            evidence={snapshot.meta.lastCrmSentAt ? `Τελευταίο CRM feedback: ${formatDateTime(snapshot.meta.lastCrmSentAt)}.` : 'Το CRM feedback περιμένει το επόμενο πραγματικό stage event.'}
            metrics={[
              { label: 'CRM sent', value: snapshot.meta.crmSent },
              { label: 'Σε ουρά', value: snapshot.meta.crmQueued },
              { label: 'Failed', value: snapshot.meta.crmFailed, tone: snapshot.meta.crmFailed > 0 ? 'danger' : 'default' },
            ]}
            href="/admin/leads/meta"
            linkLabel="Meta Lead Hub"
          />

          <IntegrationStatusCard
            title="Πρόσβαση συνεργατών"
            description="Portal passwords και frictionless exact-lead links"
            icon={<UserRoundCheck className="h-5 w-5" />}
            configured={snapshot.partnerAccess.registrationGateConfigured}
            statusLabel="Σταδιακό onboarding"
            evidence="Τα SMS και email ανοίγουν μόνο το assigned lead χωρίς login. Password χρειάζεται μόνο για το πλήρες portal."
            metrics={[
              { label: 'Με password', value: snapshot.partnerAccess.credentialedPartners },
              { label: 'Ενεργοί', value: snapshot.partnerAccess.activePartners },
              { label: 'Υιοθέτηση', value: `${accessPercent}%` },
            ]}
            href="/admin/partners"
            linkLabel="Δίκτυο συνεργατών"
          />
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]">
        <Card className="border-slate-200 shadow-none">
          <CardHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <CardTitle className="text-lg">Ταυτότητα email αποστολέα</CardTitle>
                <CardDescription className="mt-1 leading-5">Η μόνη καθημερινή ρύθμιση που αλλάζει από εδώ. SMTP host, username και password παραμένουν αποκλειστικά server-side.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveSender} className="space-y-5" noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sender-name">Όνομα αποστολέα</Label>
                  <Input id="sender-name" value={senderName} onChange={(event) => setSenderName(event.target.value)} autoComplete="organization" aria-invalid={Boolean(fieldErrors.sender_name)} className="min-h-11" />
                  {fieldErrors.sender_name && <p className="text-xs font-medium text-rose-700">{fieldErrors.sender_name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender-email">Email αποστολέα</Label>
                  <Input id="sender-email" type="email" value={senderEmail} onChange={(event) => setSenderEmail(event.target.value)} autoComplete="email" aria-invalid={Boolean(fieldErrors.sender_email)} className="min-h-11" />
                  {fieldErrors.sender_email && <p className="text-xs font-medium text-rose-700">{fieldErrors.sender_email}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs leading-5 text-slate-500">Για σωστό SPF, DKIM και DMARC alignment επιτρέπεται sender μόνο στο diathermiki.gr.</p>
                <Button type="submit" disabled={!isDirty || saving} className="min-h-11 w-full sm:w-auto">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Αποθήκευση sender
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                <KeyRound className="h-5 w-5" />
              </span>
              <div>
                <CardTitle className="text-lg">Credential operations</CardTitle>
                <CardDescription className="mt-1 leading-5">Provider consoles για συντονισμένο rotation. Η αλλαγή ολοκληρώνεται μόνο μετά από live verification.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'Twilio Console', href: 'https://console.twilio.com', icon: MessageSquare },
              { label: 'Google Cloud Console', href: 'https://console.cloud.google.com/apis/credentials', icon: MapPin },
              { label: 'Meta Business Settings', href: 'https://business.facebook.com/settings', icon: BarChart3 },
            ].map(({ label, href, icon: Icon }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex min-h-12 items-center justify-between rounded-xl border border-slate-200 px-3.5 text-sm font-semibold text-slate-700 transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
                <span className="flex items-center gap-2.5"><Icon className="h-4 w-4" />{label}</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            ))}
            <p className="pt-2 text-xs leading-5 text-slate-500">Πριν από rotation: νέο provider credential, production update, πραγματικός έλεγχος ροής και έτοιμο rollback. Δεν γίνεται αυτόματη ανάκληση από το admin.</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 bg-slate-50/70 shadow-none">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-start gap-3">
            {snapshot.readiness.attention === 0 ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />}
            <div>
              <p className="font-semibold text-slate-950">{snapshot.readiness.attention === 0 ? 'Οι κρίσιμες integrations είναι ρυθμισμένες' : `${snapshot.readiness.attention} integration χρειάζεται έλεγχο`}</p>
              <p className="mt-1 text-sm leading-5 text-slate-500">Τελευταία αλλαγή configuration: {formatDateTime(snapshot.configurationUpdatedAt)}. Για failures και retries, το Operations Inbox παραμένει η authoritative ουρά.</p>
            </div>
          </div>
          <Button asChild variant="outline" className="min-h-11 w-full shrink-0 sm:w-auto">
            <Link to="/admin/operations">Operations Inbox</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
